// ─────────────────────────────────────────────────────────────
// CSV EXPORT
//
// Converts an array of trades into an analytics-focused CSV
// and triggers a browser download.
//
// Column layout (in order):
//   Date, Time, Symbol, Direction, Entry, Exit, Size,
//   P&L (gross), Fees, Commissions, Net P&L,
//   Result, Setup, R:R, Duration, Notes
//
// "P&L" is GROSS (matches trade.pnl). "Net P&L" is a computed column —
// pnl - fees - commissions — so the file is self-documenting for tax
// prep and reconciliation, and matches what the app's analytics use
// (tradeNet) without the user having to do arithmetic in Excel.
//
// Column names are chosen to round-trip cleanly with parseCSV.
// Notes are CSV-escaped (quoted, with internal quotes doubled).
// ─────────────────────────────────────────────────────────────

// Compute net P&L using the same convention as src/lib/analytics tradeNet():
// gross pnl minus fees minus commissions. Returns "" if pnl isn't a number,
// so a row with no P&L data exports an empty Net P&L cell rather than 0.
function netPnl(t) {
  const gross = Number(t.pnl);
  if (isNaN(gross)) return "";
  const fees  = Number(t.fees) || 0;
  const comm  = Number(t.commissions) || 0;
  return String(gross - fees - comm);
}

const COLUMNS = [
  { key: "Date",        get: t => t.trade_date || "" },
  { key: "Time",        get: t => t.trade_time || t.entry_time || "" },
  { key: "Symbol",      get: t => t.symbol || "" },
  { key: "Direction",   get: t => t.direction || "" },
  { key: "Entry",       get: t => formatNum(t.entry_price) },
  { key: "Exit",        get: t => formatNum(t.exit_price) },
  { key: "Size",        get: t => formatNum(t.position_size) },
  { key: "P&L",         get: t => formatNum(t.pnl) },
  { key: "Fees",        get: t => formatNum(t.fees) },
  { key: "Commissions", get: t => formatNum(t.commissions) },
  { key: "Net P&L",     get: t => netPnl(t) },
  { key: "Result",      get: t => t.result || "" },
  { key: "Setup",       get: t => t.setup_type || t.entry_signal || "" },
  { key: "R:R",         get: t => formatNum(t.risk_reward) },
  { key: "Duration",    get: t => t.duration_minutes != null ? String(t.duration_minutes) : "" },
  { key: "Notes",       get: t => t.notes || t.post_trade_reflection || "" },
];

function formatNum(v) {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(v);
  if (isNaN(n)) return "";
  return String(n);
}

// Proper CSV-escape: wrap in quotes if value contains comma, quote, or newline;
// double any internal quotes.
function csvEscape(value) {
  if (value === null || value === undefined) return "";
  const s = String(value);
  if (/[",\r\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

export function tradesToCsv(trades) {
  const header = COLUMNS.map(c => csvEscape(c.key)).join(",");
  const rows = trades.map(t =>
    COLUMNS.map(c => csvEscape(c.get(t))).join(",")
  );
  return [header, ...rows].join("\r\n") + "\r\n";
}

// Filename: trades_YYYY-MM-DD.csv
export function makeFilename(prefix = "trades") {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm   = String(d.getMonth() + 1).padStart(2, "0");
  const dd   = String(d.getDate()).padStart(2, "0");
  return `${prefix}_${yyyy}-${mm}-${dd}.csv`;
}

// Trigger a browser download. Uses Blob + temporary anchor, no library needed.
export function downloadCsv(filename, csvContent) {
  // Prepend BOM so Excel reads UTF-8 correctly
  const bom = "\uFEFF";
  const blob = new Blob([bom + csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Free the object URL on next tick
  setTimeout(() => URL.revokeObjectURL(url), 100);
}

// ─────────────────────────────────────────────────────────────
// DATE-RANGE FILTERING
//   Mirrors the dashboard buckets so users get consistent behaviour.
// ─────────────────────────────────────────────────────────────
export function filterTradesByRange(trades, range) {
  if (range === "All" || !range) return trades;
  const cutoff = new Date();
  if      (range === "1W")  cutoff.setDate(cutoff.getDate() - 7);
  else if (range === "1M")  cutoff.setMonth(cutoff.getMonth() - 1);
  else if (range === "3M")  cutoff.setMonth(cutoff.getMonth() - 3);
  else if (range === "YTD") cutoff.setMonth(0, 1);
  else return trades;
  cutoff.setHours(0, 0, 0, 0);
  return trades.filter(t => t.trade_date && new Date(t.trade_date) >= cutoff);
}
